import { IConfirmOrderResponse, INewCreditCardResponse } from '../interfaces';
import { getSettingFromHtml } from '../../../../domain/common';
import { IResponse } from '../../../../interfaces/common-interfaces';
import { DEFAULT_ERROR } from '../../../../DAL/data-provider';
import { tiktokPurchase } from '../../../../domain/marketing/tiktok-provider';
import { ConfirmOrderResultErrorCodeEnum } from '../enums/confirm-order/confirm-order-result-error-enum';
import { InsertOrderErrorCodeEnum } from '../enums/confirm-order/insert-order-error-enum';
import { CompleteOrderResultErrorCodeEnum } from '../enums/confirm-order/complete-order-result-error-code-enum';
import { rootActions } from '../data/store';
import { CartSteps } from '../enums/cart-steps';
import { removeCartDataStorage } from '../data/cart-storage-provider';

/**
 * use zooz js API to received new credit card token and other related data
 * https://developers.paymentsos.com/docs/getting-started/collecting-card-information/using-the-secure-fields-form.html
 * https://developers.paymentsos.com/apis-and-sdks/client-library-references/securefieldsreference.html
 */
export const getNewCreditCardFromZooz = async (
    zoozSecureFields: any,
    cartHolderName: string
) : Promise<INewCreditCardResponse> => {

    let parsed = null;

    try{
        const zoozEnvironment = getSettingFromHtml('zooz-env');
        if(!zoozEnvironment){
            return {
                isError: true,
                zoozResponse: null,
                errors: null,
            };
        }

        const result = await window.POS.createToken(zoozSecureFields, {
            additionalData: {
                holder_name: cartHolderName, // This field is mandatory
            },
            environment: zoozEnvironment, // 'test' or 'live'
        });

        try{
            parsed = JSON.parse(result);
        }
        catch(ex){}

        if(!parsed){
            return {
                isError: true,
                zoozResponse: null,
                errors: result.error,
            };
        }
    }
    catch (ex){
        console.log(ex);
    }

    return {
        isError: false,
        zoozResponse: parsed,
        errors: null,
    };
};

/**
 * server returns different status codes;
 * each status code or error code may cause its own behaviour
 */
export const handleConfirmOrderResponse = (
    confirmOrderResponse: IResponse,
    setServerError: Function,
    setCartData: Function,
    isInAppPurchase: boolean,
    dispatch: Function,
) => {
    let isResponseValid = confirmOrderResponse && confirmOrderResponse.statusCode === 200;

    const hasModelValidationError =
        isResponseValid &&
        confirmOrderResponse &&
        confirmOrderResponse.data &&
        confirmOrderResponse.data.errors &&
        confirmOrderResponse.data.errors.length > 0;

    // show the first error from .net model state validation
    if(hasModelValidationError){
        // @ts-ignore - I check if errors list exists above
        setServerError(confirmOrderResponse.data.errors[0]);
        return false;
    }

    // something went wrong - there is no content in the response
    if(!confirmOrderResponse.data || !confirmOrderResponse.data.relatedData){
        setServerError(DEFAULT_ERROR);
        return false;
    }

    // make sure we take 'data.isValid' into account
    if(isResponseValid){
        isResponseValid = confirmOrderResponse.data.isValid;
    }

    const result = confirmOrderResponse.data.relatedData as IConfirmOrderResponse;

    // make sure we take 'result.isValid' into account
    if(isResponseValid){
        isResponseValid = result.isValid;
    }

    // handle error message from zooz that went through payments OS custom table filter
    if(!isResponseValid && result.zoozErrorMessage){
        setServerError(result.zoozErrorMessage);
        return false;
    }

    // general error messages from server, like zip code for USA, etc.
    if(!isResponseValid && result.errorMessage){
        setServerError(result.errorMessage);
        return false;
    }

    // here it's possible to check each of the errors by the status codes
    // result.errorCode, result.insertOrderError, result.completeOrderError
    // but most of such errors can't be shown to user ---> just general default message should be shown

    // the 'total has changed' case
    if(result.errorCode === ConfirmOrderResultErrorCodeEnum.TotalPriceChanged){
        setServerError('Your shopping cart total has changed since you last reviewed it.');

        if(result.cartData){
            setCartData(result.cartData);
        }
        return false;
    }

    if(result.errorCode === ConfirmOrderResultErrorCodeEnum.WavesUpdatePlanError){
        window.location.href = '/account/get-waves-update-plan';
        return true;
    }

    if(result.errorCode === ConfirmOrderResultErrorCodeEnum.UpgradeError){
        window.location.href = '/account/upgrades';
        return true;
    }

    // https://wavesaudio.atlassian.net/browse/DSWEB-2331
    if(result.errorCode === ConfirmOrderResultErrorCodeEnum.NoActiveItems){
        if(result.cartData){
            setCartData(result.cartData);
        }

        dispatch(
            rootActions.main({
                cartStep: CartSteps.First,
            })
        );
        return false;
    }

    // the 'is fraud' case
    if(result.insertOrderError === InsertOrderErrorCodeEnum.IsFraudError){
        setServerError('<p>You may not continue with your purchase due to too many attempts. For more details, <a href="/contact-orders" title="">contact Waves Orders Team</a>.</p>');

        if(result.cartData){
            setCartData(result.cartData);
        }
        return false;
    }

    if(result.completeOrderError === CompleteOrderResultErrorCodeEnum.InsertSubscriptionOrderError){
        removeCartDataStorage();

        // redirect: account has been charged
        window.location.href = isInAppPurchase ? '/inapp/cart/error?errorId=97' : '/cart/error?errorId=97';
        return true;
    }

    // if we arrived here, and the response is not valid ---> there is an unknown error
    if(!isResponseValid){
        setServerError(DEFAULT_ERROR);
        return false;
    }

    // all is well, the purchase went OK -----------------------

    // send tiktok purchase event
    tiktokPurchase();

    // in case of PayPal or AliPay ---> redirect
    if(result.redirect){
        window.location.href = result.redirect;
        return true;
    }

    removeCartDataStorage();

    // redirect to the thank-you page;
    // ga=1 means that we need to send 'TransactionTrigger' event in thank you page.
    // this event should be sent only once, so after sending it we remove ga=1 from the query string.
    const qs = result.orderGUID ? `?oid=${ result.orderGUID }&ga=1` : '';
    window.location.href = isInAppPurchase ? `/inapp/cart/thank-you${ qs }` : `/cart/thank-you${ qs }`;
    return true;
};
